import { IFabService, FabSearchParams, FabSearchResponse, FabAssetDetails, FabAsset } from './types';
import { FabAdapter } from '@/infrastructure/adapters/FabAdapter';

export class FabService implements IFabService {
  constructor(private readonly adapter: FabAdapter) {}

  async searchAssets(params: FabSearchParams): Promise<FabSearchResponse> {
    try {
      // Build query parameters
      const query = this.adapter.buildSearchQuery(params);
      
      // Add type=models for the search endpoint
      query.type = 'models';
      
      // Sketchfab uses /search endpoint for full-text search
      // The /models endpoint doesn't do proper text search
      const response = await this.adapter.get<unknown>('/search', query);
      
      console.log('Sketchfab API raw response:', response);
      
      // Normalize response structure (Sketchfab returns { results: [...], next, previous, cursors })
      return this.normalizeSearchResponse(response, params);
    } catch (error) {
      console.error('Error searching Fab assets:', error);
      // Return empty response on error
      return {
        assets: [],
        total: 0,
        page: params.page || 1,
        limit: params.limit || 20,
        hasMore: false,
      };
    }
  }

  async getAssetDetails(assetId: string): Promise<FabAssetDetails> {
    try {
      // Sketchfab API uses /models/{uid} endpoint for model info
      const response = await this.adapter.get<Record<string, unknown>>(`/models/${assetId}`);
      console.log('Sketchfab model details response:', response);
      
      const details = this.normalizeAssetDetails(response);
      
      // Try to get download URL from the separate download endpoint
      try {
        const downloadResponse = await this.adapter.get<Record<string, unknown>>(`/models/${assetId}/download`);
        console.log('Sketchfab download response:', downloadResponse);
        
        // Sketchfab returns { gltf: { url, size, expires }, source: { url, size, expires } }
        const gltf = downloadResponse.gltf as Record<string, unknown> | undefined;
        const glb = downloadResponse.glb as Record<string, unknown> | undefined;
        
        // Prefer GLB (single file) over GLTF (multi-file)
        const downloadUrl = glb?.url || gltf?.url;
        if (downloadUrl) {
          details.modelUrl = String(downloadUrl);
          details.downloadUrl = String(downloadUrl);
        }
      } catch {
        console.log('Download not available for this model (might require authentication or not be downloadable)');
      }
      
      return details;
    } catch (error) {
      console.error('Error fetching Fab asset details:', error);
      throw error;
    }
  }

  async downloadAsset(assetId: string): Promise<Blob> {
    try {
      // First get asset details to find download URL
      const assetDetails = await this.getAssetDetails(assetId);
      
      if (!assetDetails.downloadUrl) {
        throw new Error('Download URL not available for this asset');
      }

      // Download the asset file
      return await this.adapter.getBlob(assetDetails.downloadUrl);
    } catch (error) {
      console.error('Error downloading Fab asset:', error);
      throw error;
    }
  }

  // Normalize search response to match our interface
  private normalizeSearchResponse(response: unknown, params: FabSearchParams): FabSearchResponse {
    // Handle different possible response structures
    const data = response as Record<string, unknown>;
    
    console.log('Normalizing response, data structure:', Object.keys(data));
    
    // Sketchfab API returns: { results: [...], next, previous, cursors }
    if (data.results && Array.isArray(data.results)) {
      const results = data.results as unknown[];
      console.log(`Found ${results.length} results in response`);
      
      // Extract cursor from next URL if it exists
      const nextUrl = data.next as string | undefined;
      let nextCursor: string | undefined;
      if (nextUrl) {
        try {
          const url = new URL(nextUrl);
          nextCursor = url.searchParams.get('cursor') || undefined;
        } catch {
          // If URL parsing fails, just mark hasMore as true
        }
      }
      
      // Filter out ND licenses: processing/embedding a model counts as a derivative work
      const BLOCKED_LICENSES = new Set(['CC-BY-ND', 'CC-BY-NC-ND']);

      const normalizedAssets = results
        .map((item, index) => {
          const normalized = this.normalizeAsset(item as Record<string, unknown>, index);
          //console.log('Normalized asset:', normalized.id, normalized.title, normalized.license);
          return normalized;
        })
        .filter(asset => !asset.license || !BLOCKED_LICENSES.has(asset.license));

      return {
        assets: normalizedAssets,
        total: normalizedAssets.length,
        page: params.page || 1,
        limit: params.limit || 24,
        hasMore: !!(data.next as string), // Has more if 'next' URL exists
        nextCursor,
      };
    }

    // Try common response structures
    if (Array.isArray(data)) {
      // If response is directly an array
      return {
        assets: data.map((item, index) => this.normalizeAsset(item as Record<string, unknown>, index)),
        total: data.length,
        page: params.page || 1,
        limit: params.limit || 20,
        hasMore: false,
      };
    }

    if (data.assets && Array.isArray(data.assets)) {
      // Structure: { assets: [...], total: number }
      return {
        assets: (data.assets as unknown[]).map((item, index) => 
          this.normalizeAsset(item as Record<string, unknown>, index)
        ),
        total: (data.total as number) || 0,
        page: (data.page as number) || params.page || 1,
        limit: (data.limit as number) || params.limit || 20,
        hasMore: (data.hasMore as boolean) || (data.has_more as boolean) || false,
      };
    }

    console.warn('Unknown response structure:', data);
    // Fallback: return empty response
    return {
      assets: [],
      total: 0,
      page: params.page || 1,
      limit: params.limit || 20,
      hasMore: false,
    };
  }

  // Map Sketchfab license label strings to our short-form identifiers
  private normalizeLicenseLabel(label: string): string {
    // Exact Sketchfab API label matches
    switch (label) {
      case 'CC0': return 'CC0';
      case 'CC Attribution': return 'CC-BY';
      case 'CC Attribution-ShareAlike': return 'CC-BY-SA';
      case 'CC Attribution-NoDerivs': return 'CC-BY-ND';
      case 'CC Attribution-NonCommercial': return 'CC-BY-NC';
      case 'CC Attribution-NonCommercial-ShareAlike': return 'CC-BY-NC-SA';
      case 'CC Attribution-NonCommercial-NoDerivs': return 'CC-BY-NC-ND';
      // Sketchfab's own proprietary free license — treat as CC-BY (attribution required)
      case 'Sketchfab Standard':
      case 'Free Standard':
        return 'CC-BY';
    }
    // Fuzzy fallback for any other label variants
    const l = label.toLowerCase();
    if (l.includes('cc0') || l.includes('public domain')) return 'CC0';
    if (l.includes('noncommercial') || l.includes('non-commercial')) {
      if (l.includes('noderivs') || l.includes('no deriv')) return 'CC-BY-NC-ND';
      if (l.includes('sharealike') || l.includes('share-alike')) return 'CC-BY-NC-SA';
      return 'CC-BY-NC';
    }
    if (l.includes('noderivs') || l.includes('no deriv')) return 'CC-BY-ND';
    if (l.includes('sharealike') || l.includes('share-alike')) return 'CC-BY-SA';
    if (l.includes('attribution')) return 'CC-BY';
    // Sketchfab Standard / Free Standard fuzzy match
    if (l.includes('standard') || l.includes('sketchfab')) return 'CC-BY';
    return label;
  }

  // Normalize asset data to match our interface
  private normalizeAsset(data: Record<string, unknown>, index?: number): FabAsset {
    // Sketchfab model structure: { uid, name, description, thumbnails: { images: [...] }, user: { username }, viewerUrl, etc. }
    const thumbnails = data.thumbnails as Record<string, unknown> | undefined;
    const thumbnailImages = thumbnails?.images as unknown[] | undefined;
    
    // Select a medium-quality thumbnail (optimal for grid display, ~512x512)
    // Prefer thumbnails around 512x512 pixels for good quality without excessive file size
    let bestThumbnail: string | undefined;
    if (thumbnailImages && thumbnailImages.length > 0) {
      const targetSize = 512; // Target dimension for grid thumbnails
      let bestMatch: { url: string; diff: number } | undefined;
      
      for (const img of thumbnailImages) {
        const imgObj = img as Record<string, unknown>;
        const width = typeof imgObj.width === 'number' ? imgObj.width : 0;
        const height = typeof imgObj.height === 'number' ? imgObj.height : 0;
        
        if (imgObj.url && width > 0 && height > 0) {
          // Calculate difference from target size (use average of width/height)
          const avgSize = (width + height) / 2;
          const diff = Math.abs(avgSize - targetSize);
          
          // Prefer thumbnails between 256-1024px (reasonable range for grid)
          if (avgSize >= 256 && avgSize <= 1024) {
            if (!bestMatch || diff < bestMatch.diff) {
              bestMatch = { url: String(imgObj.url), diff };
            }
          }
        }
      }
      
      // If we found a good match, use it; otherwise fall back to largest available
      if (bestMatch) {
        bestThumbnail = bestMatch.url;
      } else {
        // Fallback: use largest available thumbnail
        let maxPixels = 0;
        for (const img of thumbnailImages) {
          const imgObj = img as Record<string, unknown>;
          const width = typeof imgObj.width === 'number' ? imgObj.width : 0;
          const height = typeof imgObj.height === 'number' ? imgObj.height : 0;
          const pixels = width * height;
          if (pixels > maxPixels && imgObj.url) {
            maxPixels = pixels;
            bestThumbnail = String(imgObj.url);
          }
        }
      }
      
      // Final fallback: first thumbnail if no dimensions found
      if (!bestThumbnail && thumbnailImages.length > 0) {
        const firstImg = thumbnailImages[0] as Record<string, unknown>;
        bestThumbnail = firstImg.url ? String(firstImg.url) : undefined;
      }
    }
    
    const user = data.user as Record<string, unknown> | undefined;

    // Parse license — Sketchfab returns an object { label, url }, not a plain string
    const licenseRaw = data.license;
    let licenseLabel: string | undefined;
    let licenseUrl: string | undefined;
    if (licenseRaw && typeof licenseRaw === 'object') {
      const licenseObj = licenseRaw as Record<string, unknown>;
      licenseLabel = licenseObj.label ? String(licenseObj.label) : undefined;
      licenseUrl = licenseObj.url ? String(licenseObj.url) : undefined;
    } else if (typeof licenseRaw === 'string' && licenseRaw) {
      licenseLabel = licenseRaw;
    }
    const license = licenseLabel ? this.normalizeLicenseLabel(licenseLabel) : undefined;

    // Build attribution string for any license that requires credit
    const assetTitle = String(data.name || data.title || 'Untitled');
    const username = user ? String(user.username || user.displayName || '') : undefined;
    // Generate attribution for any non-CC0 license, with or without author
    const attribution = license && license !== 'CC0'
      ? username
        ? `"${assetTitle}" by ${username} is licensed under ${license}${licenseUrl ? ` (${licenseUrl})` : ''}`
        : `"${assetTitle}" is licensed under ${license}${licenseUrl ? ` (${licenseUrl})` : ''}`
      : undefined;

    // Get download information if available
    const downloadData = data.download as Record<string, unknown> | undefined;
    const gltfData = downloadData?.gltf as Record<string, unknown> | undefined;
    const modelFileUrl = gltfData?.url ? String(gltfData.url) : undefined;
    
    // Get archive size info (prefer GLB format)
    const archives = data.archives as Record<string, Record<string, unknown>> | undefined;
    const glbArchive = archives?.glb;
    const gltfArchive = archives?.gltf;
    const archiveData = glbArchive || gltfArchive; // Prefer GLB
    const fileSizeBytes = archiveData?.size !== undefined ? Number(archiveData.size) : undefined;
    const textureCount = archiveData?.textureCount !== undefined ? Number(archiveData.textureCount) : undefined;
    const textureMaxRes = archiveData?.textureMaxResolution !== undefined ? Number(archiveData.textureMaxResolution) : undefined;
    
    // Ensure we always have a unique ID - use uid, id, or generate one
    const assetId = data.uid || data.id;
    const uniqueId = assetId ? String(assetId) : (index !== undefined ? `sketchfab-${index}` : `sketchfab-${Date.now()}-${Math.random()}`);
    
    // Build viewer URL from model UID
    const viewerUrl = data.uid ? `https://sketchfab.com/models/${data.uid}/embed` : undefined;
    
    return {
      id: uniqueId,
      title: String(data.name || data.title || 'Untitled'),
      description: data.description ? String(data.description) : undefined,
      thumbnailUrl: bestThumbnail || (data.thumbnailUrl ? String(data.thumbnailUrl) : undefined),
      previewUrl: data.viewerUrl ? String(data.viewerUrl) : undefined,
      downloadUrl: modelFileUrl,
      modelUrl: modelFileUrl, // Direct URL to GLB/GLTF for ModelPreview
      price: undefined, // Sketchfab models don't have price in search results
      currency: undefined,
      license,
      attribution,
      licenseUrl: licenseUrl || undefined,
      category: data.categories ? String((data.categories as unknown[])[0]) : undefined,
      tags: Array.isArray(data.tags) ? (data.tags as unknown[]).map(t => {
        const tag = t as Record<string, unknown>;
        return String(tag.slug || tag.name || t);
      }) : undefined,
      fileFormat: modelFileUrl ? (modelFileUrl.includes('.glb') ? 'glb' : 'gltf') : undefined,
      fileSize: undefined, // Not in search results
      author: user ? {
        id: String(user.uid || user.id || ''),
        name: String(user.username || user.displayName || ''),
      } : undefined,
      viewerUrl: viewerUrl || (data.viewerUrl ? String(data.viewerUrl) : (data.embedUrl ? String(data.embedUrl) : undefined)),
      // Sketchfab returns animationCount (number) rather than a boolean flag.
      // Derive isAnimated / isRigged from every known field name so client-side
      // filters don't accidentally nuke items the API already filtered for us.
      isAnimated: data.isAnimated !== undefined ? Boolean(data.isAnimated)
        : data.animated !== undefined ? Boolean(data.animated)
        : typeof data.animationCount === 'number' ? data.animationCount > 0
        : undefined,
      isRigged: data.isRigged !== undefined ? Boolean(data.isRigged)
        : data.rigged !== undefined ? Boolean(data.rigged)
        : undefined,
      faceCount: data.faceCount !== undefined ? Number(data.faceCount) : undefined,
      vertexCount: data.vertexCount !== undefined ? Number(data.vertexCount) : undefined,
      fileSizeBytes: fileSizeBytes,
      textureCount: textureCount,
      textureMaxResolution: textureMaxRes,
    };
  }

  // Normalize asset details
  private normalizeAssetDetails(data: unknown): FabAssetDetails {
    const details = data as Record<string, unknown>;
    const normalized = this.normalizeAsset(details);
    
    // Try to get download URL from details if not already set
    // Sketchfab models may have download info in different places
    if (!normalized.modelUrl) {
      // Check for download data in various possible locations
      const downloadData = details.download as Record<string, unknown> | undefined;
      const gltfData = downloadData?.gltf as Record<string, unknown> | undefined;
      const glbData = downloadData?.glb as Record<string, unknown> | undefined;
      
      // Prefer GLB over GLTF
      const modelFileUrl = glbData?.url 
        ? String(glbData.url) 
        : (gltfData?.url ? String(gltfData.url) : undefined);
      
     // console.log('Download data found:', { downloadData, gltfData, glbData, modelFileUrl });
      
      if (modelFileUrl) {
        normalized.modelUrl = modelFileUrl;
        normalized.downloadUrl = modelFileUrl;
      } else {
        console.log('No downloadable model file found for asset:', normalized.id);
      }
    }

    const relatedAssetsData = details.related_assets || details.relatedAssets;
    return {
      ...normalized,
      metadata: details.metadata as Record<string, unknown> | undefined,
      relatedAssets: Array.isArray(relatedAssetsData)
        ? (relatedAssetsData as Array<unknown>).map((item, index) => 
            this.normalizeAsset(item as Record<string, unknown>, index)
          )
        : undefined,
    };
  }
}
